import React, { useCallback, useEffect, useState } from 'react';
import { IconStarredFolder } from '@bynder/icons';
import { useTranslate } from '@bynder/localization';
import usePresetShortcuts from 'packages/hooks/shortcuts/usePresetShortcut';
import { parseShortcutForTooltip } from 'packages/common/WithShortcutLabel';
import useAccessRights from 'packages/hooks/useAccessRights';
import { useComponentsContext } from 'packages/common/Components/ComponentsContext';
import useComponentOverview from 'packages/common/Components/ComponentOverview';
import usePresets from '~/common/editor/presets/usePresets';
import generateTestId from '~/helpers/testIdHelpers';
import features from '~/configs/features';
import useEditor from '../../../../../hooks/useEditor';
import { collectSingleTopLevelGroup, collectTopLevelElements } from './utils';
import { TimelineActionsIcon } from '../TimelineActions.styled';

export default function PresetAction() {
    const { createPreset } = usePresets();
    const { manipulationRenderer } = useEditor();
    const { isPlatformAllowed } = useAccessRights();
    const hasComponentGalleryFeature = isPlatformAllowed([features.COMPONENT_GALLERY]);

    const [selectedGroup, setSelectedGroup] = useState(() =>
        collectSingleTopLevelGroup(manipulationRenderer?.getSelectedElements() || []),
    );
    const [selectedElements, setSelectedElements] = useState(() =>
        collectTopLevelElements(manipulationRenderer?.getSelectedElements() || []),
    );
    const isActive = hasComponentGalleryFeature ? selectedElements.length > 0 : !!selectedGroup;
    const { translate } = useTranslate();

    const { toggleComponentOverview } = useComponentOverview({
        onApplySuccess: () => {},
    });
    const { openComponentModal } = useComponentsContext();

    const handleCreatePreset = useCallback(() => {
        if (hasComponentGalleryFeature) {
            if (!selectedElements.length) {
                return;
            }

            openComponentModal('create', selectedElements);
        } else if (isActive) {
            createPreset(selectedGroup);
        }
    }, [isActive, createPreset, selectedGroup, hasComponentGalleryFeature, openComponentModal, selectedElements]);

    useEffect(() => {
        if (!manipulationRenderer) {
            return;
        }

        const listener = () => {
            setSelectedGroup(collectSingleTopLevelGroup(manipulationRenderer.getSelectedElements()));
            setSelectedElements(collectTopLevelElements(manipulationRenderer.getSelectedElements()));
        };

        manipulationRenderer.eventEmitter.on('elementSelected', listener);

        return () => {
            manipulationRenderer.eventEmitter.off('elementSelected', listener);
        };
    }, [manipulationRenderer]);

    usePresetShortcuts({ createPreset: handleCreatePreset });

    return (
        <div
            title={`${
                hasComponentGalleryFeature
                    ? translate('editor.timeline.element.component.save')
                    : translate('editor.timeline.element.preset.save')
            } (${parseShortcutForTooltip(['cmd', 'p'])})`}
        >
            <TimelineActionsIcon
                onClick={handleCreatePreset}
                active={isActive}
                {...generateTestId('timeline_actions_preset')}
            >
                <IconStarredFolder />
            </TimelineActionsIcon>
        </div>
    );
}
